import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { runMetrics, runMetricsStream, drillMetric, getMetricCatalog, getMetricCuration, saveMetricCuration, saveMetricOverride, getBackbone } from '../../api'
import './MetricsTab.css'
import LoadError from '../../components/LoadError'
import { loadErrorMessage } from '../../utils/loadState'
import RegisterTab from './RegisterTab'
import { downloadCard, downloadCards } from './cardExport'
import EmployeeCell from '../CustomerPortal/Lifeline/EmployeeCell'

// ─── Column display name map ──────────────────────────────────────────────────
const COL_LABELS = {
  employeeID: 'Employee ID', employeeId: 'Employee ID', employee_id: 'Employee ID',
  employeeNumber: 'Employee Number', associateOID: 'Associate OID',
  companyID: 'Company ID', companyId: 'Company ID', company_id: 'Company ID',
  companyCode: 'Company Code', companyName: 'Company Name', company_name: 'Company Name',
  firstName: 'First Name', lastName: 'Last Name', fullName: 'Name',
  employeeStatusCode: 'Status', emplStatus: 'Status',
  orgLevel1Code: 'Department', orgLevel2Code: 'Sub-Dept',
  primaryWorkLocationCode: 'Location', locationCode: 'Location', state: 'State',
  annualSalary: 'Annual Salary', hourlyPayRate: 'Hourly Rate',
  salaryOrHourlyCode: 'S/H', employeeTypeCode: 'Emp Type',
  fullTimeOrPartTimeCode: 'FT/PT', payGroup: 'Pay Group',
  payGroupDescription: 'Pay Group Name', dateOfTermination: 'Term Date',
  termReason: 'Term Reason', terminationReasonDescription: 'Term Description',
  termType: 'Term Type', statusStartDate: 'Start Date',
  originalHireDate: 'Hire Date', supervisorEmployeeNumber: 'Supervisor',
  gender: 'Gender', ethnicIDCode: 'Ethnicity', ethnicDescription: 'Ethnicity Description',
}
// localStorage key for the metric-descriptions toggle (MetricsTab toolbar).
const DESCRIPTIONS_KEY = 'wt.metricDescriptions'

function colLabel(col) {
  return COL_LABELS[col] || col.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
}

// A list metric's unit comes from its catalog entry (`value_format`: currency |
// percent | number), declared next to the SQL that produces it. It used to be a
// hand-kept set of metric keys here, which drifted: it put `$` on a percentage
// (ot_pct_of_gross_by_group) and left every finance list without one.
function formatListValue(cv, valueFormat) {
  const n = Number(cv)
  if (cv === null || cv === undefined || cv === '' || isNaN(n)) return String(cv ?? '—')
  if (valueFormat === 'currency') {
    const formatted = Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })
    return n < 0 ? `-$${formatted}` : `$${formatted}`
  }
  if (valueFormat === 'percent') return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 })
}

function getUnavailableReason(error, metric) {
  if (!error && metric?.no_data) return { msg: 'Data not available for this customer.', hint: 'This metric requires a data source that has not been connected.' }
  if (!error) return { msg: 'Run this module to see results.', hint: null }
  const e = String(error).toLowerCase()
  const tableMatch = error.match(/table[^']*'([^']+)'/i) || error.match(/does not exist.*?(\w+_(?:compensation|employment|pay|benefit|deduction|earning)\w*)/i)
  if (e.includes('does not exist') || e.includes('table not found')) {
    if (e.includes('compensation')) return { msg: 'Compensation data is not available for this customer.', hint: 'Connect a compensation data source or upload a compensation file.' }
    if (e.includes('pay_statement') || e.includes('paystatement')) return { msg: 'Pay statement data is not available.', hint: 'Upload a pay register or connect payroll to enable this metric.' }
    if (e.includes('benefit') || e.includes('deduction')) return { msg: 'Benefits or deductions data is not available.', hint: 'Upload a benefits or deductions file to enable this metric.' }
    if (e.includes('employment')) return { msg: 'Employment data is not available for this customer.', hint: 'Connect UKG Pro or upload an employment file.' }
    if (e.includes('time') || e.includes('attendance') || e.includes('punch')) return { msg: 'Time & attendance data is not available.', hint: 'Connect UKG WFM or upload a time data file.' }
    if (tableMatch) return { msg: 'Required data table is not available.', hint: 'Connect a data source or upload a file containing this data.' }
    return { msg: 'Required data is not available for this customer.', hint: 'Connect a data source or upload the required file.' }
  }
  if (e.includes('column') && (e.includes('not found') || e.includes('does not have'))) {
    const colMatch = error.match(/column[^']*'([^']+)'/i)
    const colName = colMatch ? colMatch[1] : 'a required field'
    return { msg: `This metric requires a field (${colName}) not present in this customer's data.`, hint: 'This may indicate a schema difference. Contact support or check the SQL in Settings.' }
  }
  if (e.includes('no sql') || e.includes('sql not') || !error || error === 'null') {
    return { msg: 'This metric has not been configured for this customer yet.', hint: null }
  }
  return { msg: 'This metric could not be calculated.', hint: error.length < 120 ? error : null }
}

function getMetricBadge(moduleKey, metricKey) {
  const complianceModules = ['compensation', 'compliance']
  const complianceKeys = ['flsa_breakdown', 'state_min_wage', 'pay_equity', 'salary_band_violation']
  if (complianceModules.includes(moduleKey) || complianceKeys.includes(metricKey)) return { label: 'COMP', cls: 'badge-comp' }
  if (metricKey.includes('cross') || metricKey.includes('missing')) return { label: 'CROSS', cls: 'badge-cross' }
  if (metricKey.includes('analysis') || metricKey.includes('insight')) return { label: 'LLM', cls: 'badge-llm' }
  return { label: 'DET', cls: 'badge-det' }
}

function getRisk(metric) {
  if (!metric || metric.value === null || metric.value === undefined || metric.error) return null
  const { display_type, value, key = '' } = metric
  if (display_type === 'percent') {
    const v = parseFloat(value)
    if (v > 20) return 'risk-high'
    if (v > 10) return 'risk-med'
  }
  const compKeys = ['flsa', 'min_wage', 'compliance', 'violation', 'missing', 'gap', 'below']
  if (compKeys.some(k => key.includes(k)) && parseFloat(value) > 0) return 'risk-high'
  return null
}

function formatValue(metric) {
  const { value, display_type } = metric
  if (value === null || value === undefined) return null
  if (display_type === 'percent') return `${typeof value === 'number' ? value.toFixed(1) : value}%`
  if (display_type === 'currency') return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 1 })
  return String(value)
}

function valueColorClass(risk, display_type) {
  if (risk === 'risk-high') return 'red'
  if (risk === 'risk-med') return 'amber'
  if (display_type === 'percent') return ''
  return 'green'
}

// ─── Drill Modal ──────────────────────────────────────────────────────────────
// An employee-number column, and the "<company code>|<employee number>" key the server's
// employee_refs map is keyed by (services/employee_directory.refs_for_rows): any spelling.
function isEmployeeNumberCol(col) {
  return String(col).replace(/_/g, '').toLowerCase() === 'employeenumber'
}
function rowEmployeeKey(row) {
  let code = '', num = ''
  for (const [k, v] of Object.entries(row)) {
    const role = String(k).replace(/_/g, '').toLowerCase()
    if (role === 'employeenumber' && !num && v != null) num = String(v).trim()
    if (role === 'companycode' && !code && v != null) code = String(v).trim()
  }
  return `${code}|${num}`
}

export function DrillModal({ customerId, companyName, metric, month, filters, onClose, onOpenEmployee = null }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const overlayRef = useRef(null)

  useEffect(() => {
    if (!metric) return
    setLoading(true); setData(null)
    drillMetric(customerId, metric.key, 10000, month || null, filters || null)
      .then(res => setData(res))
      .catch(err => setData({ error: err.message, rows: [], columns: [] }))
      .finally(() => setLoading(false))
  }, [customerId, metric?.key, month])

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  if (!metric) return null
  const risk = getRisk(metric)
  const summaryValueClass = risk === 'risk-high' ? 'red' : risk === 'risk-med' ? 'amber' : ''

  const ID_COLS = new Set(['emp_number','employee_number','employeeid','employeenumber',
    'applicationid','requisitionid','offerid','assignmentid','dependentid',
    'assignmentId','dependentId','applicationId','requisitionId','offerId'])

  const formatCell = (val, col = '') => {
    if (val === null || val === undefined) return <span className="wt-null">—</span>
    if (typeof val === 'number') {
      // IDs and employee numbers — never format with commas
      if (ID_COLS.has(col) || ID_COLS.has(String(col).toLowerCase())) return String(Math.round(val))
      return val > 1000 ? val.toLocaleString() : String(Math.round(val * 100) / 100)
    }
    // Strip T00:00:00 timestamp noise from date strings
    const s = String(val)
    if (/^\d{4}-\d{2}-\d{2}T00:00:00/.test(s)) return s.slice(0, 10)
    return s
  }

  const getFlagClass = (col, val) => {
    if (col.toLowerCase().includes('flsa') && String(val).toLowerCase() === 'exempt') return 'flag-red'
    if (col.toLowerCase().includes('status') && String(val).toLowerCase() === 'below') return 'flag-red'
    if (col.toLowerCase().includes('gap') && typeof val === 'number' && val < 0) return 'flag-red'
    return null
  }

  return (
    <div className="wt-modal-overlay" ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose() }}>
      <div className="wt-drill-modal" onClick={e => e.stopPropagation()}>
        <div className="wt-modal-header">
          <div>
            <div className="wt-modal-title">{metric.label}</div>
            <div className="wt-modal-meta">
            {companyName || customerId} · {metric.description}
            {month && <span style={{marginLeft:8,padding:'2px 8px',borderRadius:4,background:'var(--green-light)',color:'var(--green-dark)',fontSize:11,fontWeight:700}}>{month}</span>}
          </div>
          </div>
          <div className="wt-modal-header-right">
            {data?.rows?.length > 0 && (
              <button className="wt-download-btn" onClick={() => {
                const csv = [data.columns.join(','), ...data.rows.map(r => data.columns.map(c => `"${r[c] ?? ''}"`).join(','))].join('\n')
                const a = document.createElement('a')
                a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
                a.download = `${metric.key}_drill.csv`
                a.click()
              }}>⬇ Download CSV</button>
            )}
            <button className="wt-modal-close" onClick={onClose}>✕</button>
          </div>
        </div>
        {data && !loading && (
          <div className="wt-modal-summary">
            <div className="wt-summary-stat">
              <span className={`wt-summary-value ${summaryValueClass}`}>{data.total?.toLocaleString() ?? 0}</span>
              <span className="wt-summary-label">rows returned</span>
            </div>
            {data.columns?.length > 0 && <>
              <div className="wt-summary-divider" />
              <div className="wt-summary-stat">
                <span className="wt-summary-value">{data.columns.length}</span>
                <span className="wt-summary-label">columns</span>
              </div>
            </>}
            <span className="wt-scope-pill">{companyName || customerId}</span>
          </div>
        )}
        <div className="wt-modal-table-wrap">
          {loading && <div className="wt-modal-loading"><div className="wt-spinner" /><span>Loading employee data…</span></div>}
          {!loading && data?.error && <div className="wt-modal-error">⚠ {data.error}</div>}
          {!loading && !data?.error && data?.rows?.length === 0 && <div className="wt-modal-empty">{drillEmptyText(data)}</div>}
          {!loading && !data?.error && data?.rows?.length > 0 && (
            <table className="wt-drill-table">
              <thead><tr>{data.columns.map(col => <th key={col}>{colLabel(col)}</th>)}</tr></thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={i}>
                    {data.columns.map(col => {
                      const flagCls = getFlagClass(col, row[col])
                      return (
                        <td key={col} className={String(col).toLowerCase().includes('number') || String(col).toLowerCase().includes('id') ? 'td-mono' : ''}>
                          {isEmployeeNumberCol(col) && onOpenEmployee
                            ? <EmployeeCell number={row[col]} employeeRef={data.employee_refs?.[rowEmployeeKey(row)]}
                                            onOpenEmployee={(ref) => { onClose(); onOpenEmployee(ref) }} />
                            : flagCls ? <span className={`td-flag ${flagCls}`}>{formatCell(row[col], col)}</span> : formatCell(row[col], col)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

// The identity of a chart point, sent to its drill: the card's point_key when it returns
// one (a period), else its label when that is a month or a pay date. A period card always
// returns point_key; a row without one is a card computed before it did, and its label
// (a period name) does not identify the period -- null, and the card asks to be refreshed
// rather than drilling to the wrong rows.
const POINT_LABEL = /^\d{4}-\d{2}(-\d{2})?$/
function pointOf(row, labelKey) {
  if (row?.point_key != null) return String(row.point_key)
  const label = String(row?.[labelKey] ?? '')
  return POINT_LABEL.test(label) ? label : null
}
const STALE_POINT = 'Refresh the card to drill into a single period.'

// An empty drill is either an answer (nothing matched) or a refusal; a refusal says why.
const DRILL_REFUSAL_TEXT = {
  month_filter_unplaceable: "This drill can't be narrowed to that month, so it shows no rows rather than rows from other months.",
  invalid_month: "That isn't a point on this chart.",
  month_not_applicable: "This list isn't broken down by month.",
}

function drillEmptyText(data) {
  return DRILL_REFUSAL_TEXT[data?.reason] || data?.message || 'No records found for this metric.'
}

// ─── Edit Panel ───────────────────────────────────────────────────────────────
export function EditPanel({ metric, customerId, companyName, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState('params')
  const [label, setLabel] = useState(metric?.label_override || metric?.label || '')
  const [displayType, setDisplayType] = useState(metric?.display_type_override || metric?.display_type || 'number')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  if (!metric) return null

  const isDirty = label !== (metric.label_override || metric.label || '') ||
                  displayType !== (metric.display_type_override || metric.display_type || 'number')

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await saveMetricOverride(customerId, metric.key, {
        module_key: metric.module_key || '',
        label_override: label !== metric.label ? label : null,
        display_type_override: displayType !== metric.display_type ? displayType : null,
      })
      setSaved(true)
      if (onSaved) onSaved(metric.key, { label, display_type: displayType })
      setTimeout(() => { setSaved(false); onClose() }, 1200)
    } catch (e) {
      setError('Save failed — please try again')
    } finally {
      setSaving(false)
    }
  }

  const handleRestore = async () => {
    setSaving(true)
    try {
      await saveMetricOverride(customerId, metric.key, {
        module_key: metric.module_key || '',
        label_override: null,
        display_type_override: null,
      })
      if (onSaved) onSaved(metric.key, { label: metric.label, display_type: metric.display_type })
      onClose()
    } catch (e) {
      setError('Restore failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="wt-edit-overlay">
      <div className="wt-edit-backdrop" onClick={onClose} />
      <div className="wt-edit-panel">
        <div className="wt-edit-header">
          <div>
            <div className="wt-edit-title">{metric.label}</div>
            <div className="wt-edit-subtitle">{companyName || customerId} · Metric Configuration</div>
          </div>
          <button className="wt-edit-close" onClick={onClose}>✕</button>
        </div>
        <div className="wt-edit-tabs">
          <div className={`wt-edit-tab ${activeTab === 'params' ? 'active' : ''}`} onClick={() => setActiveTab('params')}>
            Parameters <span className="wt-role-pill pill-customer">Customer</span>
          </div>
          <div className={`wt-edit-tab ${activeTab === 'mapping' ? 'active' : ''}`} onClick={() => setActiveTab('mapping')}>
            Column Mapping <span className="wt-role-pill pill-consultant">Consultant</span>
          </div>
          <div className={`wt-edit-tab ${activeTab === 'sql' ? 'active' : ''}`} onClick={() => setActiveTab('sql')}>
            SQL Override <span className="wt-role-pill pill-consultant">Consultant</span>
          </div>
        </div>
        <div className="wt-edit-body">
          {activeTab === 'params' && (
            <div className="wt-edit-section">
              <div className="wt-edit-section-title">Metric Configuration</div>
              <div className="wt-param-row">
                <div className="wt-param-info"><div className="wt-param-name">Display Label</div><div className="wt-param-desc">Name shown on the card</div></div>
                <div className="wt-param-input">
                  <input className="wt-param-text" type="text" value={label} onChange={e => setLabel(e.target.value)} />
                </div>
              </div>
              <div className="wt-param-row">
                <div className="wt-param-info"><div className="wt-param-name">Display Type</div><div className="wt-param-desc">How the result value is rendered</div></div>
                <div className="wt-param-input">
                  <select className="wt-param-select" value={displayType} onChange={e => setDisplayType(e.target.value)}>
                    <option value="number">Number</option>
                    <option value="percent">Percent</option>
                    <option value="currency">Currency</option>
                    <option value="list">List / Breakdown</option>
                    <option value="time_series">Time Series</option>
                  </select>
                </div>
              </div>
              <div style={{ padding: '12px 0', fontSize: '11.5px', color: 'var(--gray-400)' }}>Platform-managed metric · Parameters updated per customer</div>
              {error && <div style={{ color: 'var(--red)', fontSize: '12px', padding: '4px 0' }}>{error}</div>}
            </div>
          )}
          {activeTab === 'mapping' && (
            <div className="wt-edit-section">
              <div className="wt-edit-section-title">Field Mappings</div>
              <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginBottom: '12px', lineHeight: 1.6 }}>Columns auto-resolved from this customer's DuckDB schema. Manual overrides persist per customer.</div>
              <div className="wt-col-map-row"><div className="wt-col-map-label">Emp Status</div><div className="wt-col-map-arrow">→</div><div className="wt-col-map-value">employeeStatusCode</div><span className="wt-col-map-status col-status-auto">Auto</span><button className="wt-col-edit-btn">✎</button></div>
              <div className="wt-col-map-row"><div className="wt-col-map-label">Hire Date</div><div className="wt-col-map-arrow">→</div><div className="wt-col-map-value">originalHireDate</div><span className="wt-col-map-status col-status-auto">Auto</span><button className="wt-col-edit-btn">✎</button></div>
            </div>
          )}
          {activeTab === 'sql' && (
            <div className="wt-edit-section">
              <div className="wt-edit-section-title">Base Template SQL</div>
              <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginBottom: '10px', lineHeight: 1.5 }}>Platform default SQL for this metric. Editing creates a customer-specific override.</div>
              <div className="wt-sql-toolbar"><span className="wt-sql-toolbar-label">SQL Editor</span><button className="wt-sql-validate-btn">Validate SQL</button></div>
              <textarea className="wt-sql-editor" spellCheck={false} defaultValue={metric.sql || '-- SQL not available for this metric'} />
              <div className="wt-sql-note"><span>🔒</span> Consultant-only. Changes create a per-customer override.</div>
            </div>
          )}
        </div>
        <div className="wt-edit-footer">
          <button className="wt-reset-btn" onClick={handleRestore} disabled={saving}>Restore Default</button>
          <button className="wt-cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className={`wt-save-btn${saved ? ' wt-save-btn--saved' : ''}`}
            onClick={handleSave}
            disabled={saving || saved}
          >
            {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Curation Panel ───────────────────────────────────────────────────────────
// Flat list of all metrics across all modules. Consultant toggles visibility
// and reorders. Saved state is inherited by the portal.
function CurationPanel({ catalog, curation, onSave, onClose, customerId }) {
  // Build a flat ordered list from current curation + catalog defaults
  const buildList = () => {
    const curMap = {}
    ;(curation.entries || []).forEach(e => { curMap[e.metric_key] = e })

    const list = []
    let globalOrder = 0
    Object.entries(catalog).forEach(([moduleKey, mod]) => {
      Object.entries(mod.metrics || {}).forEach(([metricKey, def]) => {
        const saved = curMap[metricKey]
        list.push({
          metric_key: metricKey,
          module_key: moduleKey,
          label: def.label,
          module_label: mod.label,
          visible: saved ? saved.visible : true,
          sort_order: saved ? saved.sort_order : globalOrder,
        })
        globalOrder++
      })
    })
    return list.sort((a, b) => a.sort_order - b.sort_order)
  }

  const [items, setItems] = useState(buildList)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const dragIdx = useRef(null)

  const toggleVisible = (metricKey) => {
    setItems(prev => prev.map(it => it.metric_key === metricKey ? { ...it, visible: !it.visible } : it))
    setDirty(true)
  }

  const move = (idx, dir) => {
    setItems(prev => {
      const next = [...prev]
      const target = idx + dir
      if (target < 0 || target >= next.length) return prev
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next.map((it, i) => ({ ...it, sort_order: i }))
    })
    setDirty(true)
  }

  const handleDragStart = (e, idx) => {
    dragIdx.current = idx
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDrop = (e, dropIdx) => {
    e.preventDefault()
    const from = dragIdx.current
    if (from === null || from === dropIdx) return
    setItems(prev => {
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(dropIdx, 0, moved)
      return next.map((it, i) => ({ ...it, sort_order: i }))
    })
    dragIdx.current = null
    setDirty(true)
  }

  const handleSave = async () => {
    setSaving(true)
    const entries = items.map(it => ({
      metric_key: it.metric_key,
      module_key: it.module_key,
      visible: it.visible,
      sort_order: it.sort_order,
    }))
    try {
      await saveMetricCuration(customerId, entries)
      onSave(entries)
      setDirty(false)
    } catch (e) {
      console.error('Curation save failed:', e)
    }
    setSaving(false)
  }

  const visibleCount = items.filter(it => it.visible).length
  const totalCount = items.length

  // Group by module for display
  const byModule = {}
  items.forEach((it, idx) => {
    if (!byModule[it.module_key]) byModule[it.module_key] = []
    byModule[it.module_key].push({ ...it, _idx: idx })
  })

  return (
    <div className="wt-curation-overlay">
      <div className="wt-curation-backdrop" onClick={onClose} />
      <div className="wt-curation-panel">
        <div className="wt-curation-header">
          <div>
            <div className="wt-curation-title">Metrics Curation</div>
            <div className="wt-curation-subtitle">
              {visibleCount} of {totalCount} shown · Portal inherits this configuration
            </div>
          </div>
          <button className="wt-edit-close" onClick={onClose}>✕</button>
        </div>

        <div className="wt-curation-hint">
          Toggle visibility and drag rows to reorder. The portal will show metrics in this order.
        </div>

        <div className="wt-curation-body">
          {Object.entries(byModule).map(([moduleKey, mItems]) => {
            const mod = catalog[moduleKey]
            return (
              <div key={moduleKey} className="wt-curation-module">
                <div className="wt-curation-module-header">
                  <span className="wt-curation-module-icon">{mod?.icon}</span>
                  {mod?.label}
                  <span className="wt-curation-module-count">
                    {mItems.filter(it => it.visible).length}/{mItems.length}
                  </span>
                </div>
                {mItems.map((it) => (
                  <div
                    key={it.metric_key}
                    className={`wt-curation-row ${!it.visible ? 'wt-curation-hidden' : ''}`}
                    draggable
                    onDragStart={e => handleDragStart(e, it._idx)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => handleDrop(e, it._idx)}
                  >
                    <span className="wt-curation-drag">⠿</span>
                    <button
                      className={`wt-curation-eye ${it.visible ? 'visible' : 'hidden'}`}
                      onClick={() => toggleVisible(it.metric_key)}
                      title={it.visible ? 'Hide from portal' : 'Show in portal'}
                    >
                      {it.visible ? '👁' : '👁\u{FE0F}'}
                    </button>
                    <span className={`wt-curation-label ${!it.visible ? 'wt-curation-label-dim' : ''}`}>
                      {it.label}
                    </span>
                    <span className="wt-curation-key">{it.metric_key}</span>
                    <div className="wt-curation-arrows">
                      <button onClick={() => move(it._idx, -1)} title="Move up">↑</button>
                      <button onClick={() => move(it._idx, 1)} title="Move down">↓</button>
                    </div>
                  </div>
                ))}
              </div>
            )
          })}
        </div>

        <div className="wt-curation-footer">
          <button className="wt-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="wt-save-btn"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? 'Saving…' : `Save Curation${dirty ? ' *' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Metric Card ──────────────────────────────────────────────────────────────
export function MetricCard({ metric, moduleKey, customerId, filters = null, onDrill, onEdit, showDescriptions = true, selecting = false, selected = false, onToggleSelect = null }) {
  const { key, label, value, display_type, error, status_scope } = metric
  // The plain-English description shows ONCE, directly under the number (or under the
  // title on list / trend cards) -- Scott 2026-10-01: not again in the footer. The
  // descriptions toggle (MetricsTab toolbar) hides it; the portal renders this same
  // component, so it behaves the same.
  const description = showDescriptions ? metric.description : null
  const hasValue = value !== null && value !== undefined && !error
  const isLoading = metric._loading
  const badge = getMetricBadge(moduleKey, key || '')
  const risk = getRisk({ ...metric, key })
  const colorCls = hasValue ? valueColorClass(risk, display_type) : ''
  const isFilterIndependent = status_scope === 'terminated' || status_scope === 'all'
  const unfilteredCls = isFilterIndependent ? 'wt-metric-card--unfiltered' : ''
  const filterBadge = isFilterIndependent
    ? <span className="wt-filter-independent-badge" title="This metric is not affected by the active filters">⊘ filter-independent</span>
    : null
  // Persistent ref to capture clicked month from Recharts before outer div onClick fires
  const clickedMonthRef = useRef(null)
  const [pointNote, setPointNote] = useState(null)

  // A card can opt out of drilling (catalog "drillable": false) -- no button, click does nothing.
  const drillable = metric.drillable !== false
  const handleDrillClick = (e) => { e.stopPropagation(); if (drillable) onDrill(metric) }
  const handleEditClick = (e) => { e.stopPropagation(); onEdit(metric) }

  // Download = the card's data as Excel + a picture of the card (cardExport.js).
  // The filters MUST match what the card was computed with (MetricsTab passes the
  // same buildEffectiveFilters() result the drill panel gets). Passing null once
  // shipped a file of a different population than the card on screen: measured
  // 2026-08-05 at a live tenant, headcount_by_dept with an org_level_2 filter -> the
  // exported file held roughly twice the card's population. cardRows() keeps that contract for single and multi downloads.
  const [exporting, setExporting] = useState(false)
  const handleExportClick = async (e) => {
    e.stopPropagation()
    const el = e.currentTarget.closest('.wt-metric-card')
    setExporting(true)
    try { await downloadCard({ customerId, metric: { ...metric, key, label }, filters, el }) }
    finally { setExporting(false) }
  }
  const selectBox = selecting ? (
    <label className="wt-card-select" onClick={e => e.stopPropagation()} title="Select for download">
      <input type="checkbox" checked={selected} onChange={() => onToggleSelect && onToggleSelect(key)} />
    </label>
  ) : null
  const downloadBtn = (
    <button className="wt-card-action-btn" title="Download (Excel + picture)" onClick={handleExportClick} disabled={exporting}>
      {exporting ? '…' : '⬇'}
    </button>
  )
  const cardClick = (e) => {
    if (selecting) { e.stopPropagation(); if (onToggleSelect) onToggleSelect(key); return }
    handleDrillClick(e)
  }

  if (display_type === 'list' && hasValue && Array.isArray(value)) {
    const rows = value.slice(0, 6)
    // Bars scale on magnitude so negative values (variances) still draw.
    const maxVal = rows.length > 0 ? Math.max(...rows.map(r => Math.abs(Number(Object.values(r)[1])) || 0)) : 1
    return (
      <div className={`wt-metric-card has-result ${unfilteredCls} ${risk || ''} ${selected ? 'wt-metric-card--selected' : ''}`} data-metric-key={key} onClick={cardClick}>
        <div className="wt-metric-card-header">
          {selectBox}
          <div className="wt-metric-card-name">{label}</div>
          <span className={`wt-metric-type-badge ${badge.cls}`}>{badge.label}</span>
        </div>
        {description && <div className="wt-card-desc">{description}</div>}
        <div className="wt-metric-result">
          <div className="wt-list-rows">
            {rows.map((row, i) => {
              const vals = Object.values(row)
              const lv = String(vals[0] ?? '—'); const cv = vals[1]
              const shown = formatListValue(cv, metric.value_format)
              const pct = maxVal > 0 ? (Math.abs(Number(cv)) / maxVal) * 100 : 0
              return (
                <div key={i} className="wt-list-row" title={`${lv}: ${shown}`}>
                  <span className="wt-list-label">{lv}</span>
                  <div className="wt-list-bar-wrap"><div className="wt-list-bar" style={{ width: `${pct || 0}%` }} /></div>
                  <span className="wt-list-count">{shown}</span>
                </div>
              )
            })}
          </div>
          {drillable && <button className="wt-result-drill" onClick={handleDrillClick}>View rows ›</button>}
        </div>
        <div className="wt-metric-card-footer">
          <div className="wt-footer-scope" />
          <div className="wt-card-actions">
            {downloadBtn}
            <button className="wt-card-action-btn" title="Settings" onClick={handleEditClick}>⚙</button>
          </div>
        </div>
      </div>
    )
  }

  if (display_type === 'time_series' && hasValue && Array.isArray(value)) {
    const sample = value[0] || {}
    const keys = Object.keys(sample)
    const labelKey = keys[0]
    const seriesKey = keys.find(k => k === 'series')
    const valueKey = keys.find(k => /pct|rate|percent/i.test(k)) || (keys.includes('value') ? 'value' : keys[keys.length - 1])
    const isPct = /pct|rate|percent/i.test(valueKey)
    const fmt = (v) => isPct ? `${(+v).toFixed(2)}%` : Math.round(+v).toLocaleString()
    // Axis ticks stay short (400k, 1.2M) so they fit the axis width instead of
    // running off the card's left edge.
    const fmtTick = (v) => {
      if (isPct) return `${v}%`
      const a = Math.abs(v)
      if (a >= 1e9) return `${+(v / 1e9).toFixed(1)}B`
      if (a >= 1e6) return `${+(v / 1e6).toFixed(1)}M`
      if (a >= 1e3) return `${+(v / 1e3).toFixed(0)}k`
      return v
    }
    const PALETTE = ['#42b8fc','#031244','#f59e0b','#10b981','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316','#6366f1','#84cc16']

    let chartEl, latestVal, avgVal
    if (seriesKey) {
      const seriesNames = [...new Set(value.map(r => String(r[seriesKey] ?? '')))].sort()
      const byX = {}
      value.forEach(r => {
        const x = String(r[labelKey] || '').slice(0, 10)
        if (!byX[x]) byX[x] = { month: x, point: pointOf(r, labelKey) }
        byX[x][String(r[seriesKey] ?? '')] = parseFloat(r[valueKey]) || 0
      })
      const chartData = Object.values(byX).sort((a, b) => a.month < b.month ? -1 : 1)
      const totals = chartData.map(row => seriesNames.reduce((s, n) => s + (row[n] || 0), 0))
      latestVal = totals[totals.length - 1] || 0
      avgVal = totals.length ? totals.reduce((s, t) => s + t, 0) / totals.length : 0
      const maxV = Math.max(...value.map(r => parseFloat(r[valueKey]) || 0), 0.1)
      chartEl = (
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          onClick={(rd) => { if (rd?.activePayload?.[0]) { const pl = rd.activePayload[0].payload; clickedMonthRef.current = pl?.point ?? { stale: true } } }}
          style={{ cursor: 'pointer' }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={20} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false}
            width={44} tickFormatter={fmtTick} domain={[0, Math.ceil(maxV * 1.15)]} />
          <Tooltip formatter={(v, n) => [fmt(v), n]} contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e5e7eb' }} />
          {seriesNames.map((s, i) => (
            <Line key={s} type="monotone" dataKey={s} stroke={PALETTE[i % PALETTE.length]} strokeWidth={1.5} dot={false} activeDot={{ r: 4 }} connectNulls />
          ))}
        </LineChart>
      )
    } else {
      const chartData = value.map(row => ({ month: String(row[labelKey] || '').slice(0, 10), point: pointOf(row, labelKey), rate: parseFloat(row[valueKey]) || 0 }))
      const maxRate = Math.max(...chartData.map(d => d.rate), 0.1)
      latestVal = chartData[chartData.length - 1]?.rate || 0
      avgVal = chartData.length ? chartData.reduce((s, d) => s + d.rate, 0) / chartData.length : 0
      chartEl = (
        <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          onClick={(rd) => { if (rd?.activePayload?.[0]) { const pl = rd.activePayload[0].payload; clickedMonthRef.current = pl?.point ?? { stale: true } } }}
          style={{ cursor: 'pointer' }}>
          <defs>
            <linearGradient id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#42b8fc" stopOpacity={0.28}/>
              <stop offset="95%" stopColor="#42b8fc" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false}
            width={44} tickFormatter={fmtTick} domain={[0, Math.ceil(maxRate * 1.2)]} />
          <Tooltip formatter={(v) => [fmt(v), valueKey]} contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e5e7eb' }} />
          <Area type="monotone" dataKey="rate" stroke="#42b8fc" strokeWidth={2} fill={`url(#grad-${key})`}
            dot={{ r: 3, fill: '#42b8fc', cursor: 'pointer' }} activeDot={{ r: 6, cursor: 'pointer' }} />
        </AreaChart>
      )
    }
    return (
      <div className={`wt-metric-card wt-metric-card--wide has-result ${unfilteredCls} ${risk || ''} ${selected ? 'wt-metric-card--selected' : ''}`} data-metric-key={key}
        onClick={(e) => {
          const m = clickedMonthRef.current
          clickedMonthRef.current = null
          if (selecting) { cardClick(e); return }
          if (m?.stale) { setPointNote(STALE_POINT); return }
          setPointNote(null)
          if (m) { onDrill({ ...metric, key, _month: m }) } else { handleDrillClick(e) }
        }}
        title="Click chart to filter by date · Click card to view all"
      >
        <div className="wt-metric-card-header">
          {selectBox}
          <div className="wt-metric-card-name">{label}</div>
          <span className={`wt-metric-type-badge ${badge.cls}`}>{badge.label}</span>
        </div>
        {description && <div className="wt-card-desc">{description}</div>}
        <div className="wt-metric-chart">
          <ResponsiveContainer width="100%" height={110}>
            {chartEl}
          </ResponsiveContainer>
          <div className="wt-chart-summary">
            <span className="wt-chart-latest">Latest: <strong>{fmt(latestVal)}</strong></span>
            <span className="wt-chart-avg">Avg / date: <strong>{fmt(avgVal)}</strong></span>
          </div>
        </div>
        {pointNote && <div className="wt-card-desc" role="status">{pointNote}</div>}
        <div className="wt-metric-card-footer">
          <div className="wt-footer-scope" />
          <div className="wt-card-actions">
            {downloadBtn}
            <button className="wt-card-action-btn" title="Settings" onClick={handleEditClick}>⚙</button>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="wt-metric-card">
        <div className="wt-metric-card-header">
          <div className="wt-metric-card-name">{label}</div>
          <span className={`wt-metric-type-badge ${badge.cls}`}>{badge.label}</span>
        </div>
        {description && <div className="wt-card-desc">{description}</div>}
        <div className="wt-metric-loading"><div className="wt-skeleton" /><div className="wt-skeleton wt-skeleton-sm" /></div>
        <div className="wt-metric-card-footer">
          <div className="wt-footer-scope" style={{ color: 'var(--blue)' }}>Running…</div>
          <div className="wt-card-actions"><button className="wt-card-action-btn" onClick={handleEditClick}>⚙</button></div>
        </div>
      </div>
    )
  }

  if (error || !hasValue) {
    const { msg, hint } = getUnavailableReason(error, metric)
    return (
      <div className="wt-metric-card wt-metric-card--unavailable">
        <div className="wt-metric-card-header">
          <div className="wt-metric-card-name">{label}</div>
          <span className={`wt-metric-type-badge ${badge.cls}`}>{badge.label}</span>
        </div>
        {description && <div className="wt-card-desc">{description}</div>}
        <div className="wt-metric-unavailable">
          <div className="wt-unavailable-msg">{msg}</div>
          {hint && <div className="wt-unavailable-hint">{hint}</div>}
        </div>
        <div className="wt-metric-card-footer">
          <div className="wt-footer-scope" />
          <div className="wt-card-actions"><button className="wt-card-action-btn" onClick={handleEditClick}>⚙</button></div>
        </div>
      </div>
    )
  }

  if (display_type === 'stacked_bar' && hasValue && Array.isArray(value)) {
    const rows = value.filter(r => {
      const vals = Object.values(r)
      return vals.slice(1).some(v => Number(v) > 0)
    })
    // keys after 'label' are the stack segments (M, F, Other etc.)
    const sample = rows[0] || {}
    const keys = Object.keys(sample).filter(k => k !== 'label')
    const COLORS = { M: '#031244', F: '#42b8fc', Other: '#6b9bd1', Unknown: '#bcd9f2' }
    const defaultColors = ['#031244','#42b8fc','#6b9bd1','#bcd9f2','#1d4f9e']
    const chartData = rows.map(r => {
      const obj = { label: String(Object.values(r)[0] ?? '') }
      keys.forEach(k => { obj[k] = Number(r[k]) || 0 })
      return obj
    })
    return (
      <div className={`wt-metric-card has-result ${unfilteredCls} ${risk || ''} ${selected ? 'wt-metric-card--selected' : ''}`} data-metric-key={key} onClick={cardClick}>
        <div className="wt-metric-card-header">
          {selectBox}
          <div className="wt-metric-card-name">{label}</div>
          <span className={`wt-metric-type-badge ${badge.cls}`}>{badge.label}</span>
        </div>
        {description && <div className="wt-card-desc">{description}</div>}
        <div className="wt-metric-result" style={{ paddingTop: 4 }}>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={90} tickLine={false} axisLine={false}
                tickFormatter={v => (String(v).length > 14 ? `${String(v).slice(0, 13)}…` : v)} />
              <Tooltip formatter={(v, name) => [v, name]} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              {keys.map((k, i) => (
                <Bar key={k} dataKey={k} stackId="a" fill={COLORS[k] || defaultColors[i % defaultColors.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
          {drillable && <button className="wt-result-drill" onClick={handleDrillClick}>View rows ›</button>}
        </div>
        <div className="wt-metric-card-footer">
          <div className="wt-footer-scope" />
          <div className="wt-card-actions">{downloadBtn}<button className="wt-card-action-btn" onClick={handleEditClick}>⚙</button></div>
        </div>
      </div>
    )
  }


  const formatted = formatValue(metric)
  return (
    <div className={`wt-metric-card has-result ${unfilteredCls} ${risk || ''} ${selected ? 'wt-metric-card--selected' : ''}`} data-metric-key={key} onClick={cardClick}>
      <div className="wt-metric-card-header">
        {selectBox}
        <div className="wt-metric-card-name">{label}</div>
        <span className={`wt-metric-type-badge ${badge.cls}`}>{badge.label}</span>
      </div>
      <div className="wt-metric-result">
        <div className={`wt-result-value ${colorCls}`}>{formatted}</div>
        {description && <div className="wt-result-label">{description}</div>}
        {drillable && <button className="wt-result-drill" onClick={handleDrillClick}>View rows ›</button>}
      </div>
      <div className="wt-metric-card-footer">
        <div className="wt-footer-scope">
          {metric.source_column ? <>Via <strong>{metric.source_column}</strong></> : null}
        </div>
        <div className="wt-card-actions">
          {downloadBtn}
          <button className="wt-card-action-btn" title="Settings" onClick={handleEditClick}>⚙</button>
        </div>
      </div>
    </div>
  )
}

// ─── Effective filter helper ──────────────────────────────────────────────────
export function buildEffectiveFilters(activeFilters, filterDataResult) {
  if (!activeFilters) return activeFilters
  // pay_date is a Register/period-drill filter — metric aggregates have their
  // own time scoping (YTD, rolling 12mo) and must never be restricted to a
  // single pay date. Strip it unconditionally before anything else.
  const withoutPayDate = Object.fromEntries(
    Object.entries(activeFilters).filter(([k]) => k !== 'pay_date')
  )
  if (!filterDataResult?.filters) return withoutPayDate
  const effective = {}
  for (const [key, selectedValues] of Object.entries(withoutPayDate)) {
    if (!selectedValues || selectedValues.length === 0) continue
    const filterDef = filterDataResult.filters[key]
    if (!filterDef) { effective[key] = selectedValues; continue }
    if (filterDef.single_select) { effective[key] = selectedValues; continue }
    const allOptions = (filterDef.options || []).map(o => o.value)
    const selSet = new Set(selectedValues)
    if (allOptions.every(v => selSet.has(v))) continue
    effective[key] = selectedValues
  }
  return effective
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MetricsTab({ customerId, companyName = null, activeFilters = {}, filterData = null, consultantMode = false, onModuleChange = null, onOpenEmployee = null }) {
  const coName = companyName || customerId
  const [catalog, setCatalog] = useState(null)
  // A failed catalog read is an error with a Retry, never an endless "Loading…" (item 201).
  const [catalogError, setCatalogError] = useState(null)
  const [catalogTry, setCatalogTry] = useState(0)
  // Show / hide the plain-English metric descriptions. Per browser, remembered across
  // visits; on by default. Same component in the consultant view and the portal.
  const [showDescriptions, setShowDescriptions] = useState(() => {
    try { return localStorage.getItem(DESCRIPTIONS_KEY) !== 'off' } catch { return true }
  })
  const toggleDescriptions = () => setShowDescriptions(prev => {
    const next = !prev
    try { localStorage.setItem(DESCRIPTIONS_KEY, next ? 'on' : 'off') } catch { /* storage blocked: toggle still works this visit */ }
    return next
  })
  const [presentDomains, setPresentDomains] = useState(null)   // data-present domains from /api/backbone; null = not yet resolved
  const [curation, setCuration] = useState({ entries: [] })
  const [results, setResults] = useState({})
  const [loading, setLoading] = useState(false)
  const [activeModule, setActiveModule] = useState(null)
  const [drillTarget, setDrillTarget] = useState(null)
  const [editTarget, setEditTarget] = useState(null)
  const [showCuration, setShowCuration] = useState(false)
  const [lastRun, setLastRun] = useState(null)
  const [railQuery, setRailQuery] = useState('')
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set())

  // ONE source for the active filter set. The card values, the drill panel and the
  // CSV export must all be the same population; they diverged (#50) precisely because
  // the export computed its own (null) filter set instead of sharing this one.
  const effectiveFilters = useMemo(
    () => buildEffectiveFilters(activeFilters, filterData),
    [activeFilters, filterData]
  )

  // Load catalog + curation + derived present-domains in parallel
  useEffect(() => {
    setCatalogError(null)
    Promise.all([
      getMetricCatalog(),
      getMetricCuration(customerId).catch(() => ({ entries: [] })),
      getBackbone(customerId).catch(() => ({ present_domains: null })),
    ]).then(([catalogData, curationData, bb]) => {
      const pd = (bb && Array.isArray(bb.present_domains)) ? bb.present_domains : null
      setPresentDomains(pd)
      setCatalog(catalogData)
      setCuration(curationData || { entries: [] })
      // First tab = first module in a PRESENT domain (data-present floor), not the
      // global catalog's first module — so HCM-only customers don't open on a WFM tab.
      // `data_source` (backbone vocabulary), never `domain`: every wfm_* module is domain=hcm.
      const isPresent = (m) => !!m && (m._synthetic || !pd || pd.includes(m.data_source || 'hcm'))
      const firstKey = Object.keys(catalogData).find(k => isPresent(catalogData[k])) || Object.keys(catalogData)[0]
      if (firstKey) { setActiveModule(firstKey); if (onModuleChange) onModuleChange(firstKey); }
      // Inject synthetic register tab — not a catalog metric module. Shown to every viewer:
      // RegisterTab reads the customer-scoped register endpoints (periods, checks, check detail).
      catalogData['register'] = { label: 'Register', icon: '🧾', metrics: {}, _synthetic: true }
    }).catch(err => { console.error(err); setCatalogError(loadErrorMessage(err)) })
  }, [customerId, catalogTry])

  // Build curation lookup: metric_key → { visible, sort_order }
  const curationMap = {}
  ;(curation.entries || []).forEach(e => { curationMap[e.metric_key] = e })
  const hasCuration = curation.entries && curation.entries.length > 0

  // Apply curation to a module's metric list
  const applyModuleCuration = (moduleKey, rawMetrics) => {
    if (!hasCuration) return rawMetrics

    // Filter to visible only, then sort by curation order
    const visible = rawMetrics.filter(([key]) => {
      const c = curationMap[key]
      return !c || c.visible !== false
    })

    return visible.sort(([aKey], [bKey]) => {
      const aOrder = curationMap[aKey]?.sort_order ?? 9999
      const bOrder = curationMap[bKey]?.sort_order ?? 9999
      return aOrder - bOrder
    })
  }

  const runModule = useCallback(async (moduleKey, filtersOverride, filterDataOverride) => {
    if (!catalog) return
    if (catalog[moduleKey]?._synthetic) return  // register tab — no metrics to run
    const rawMetrics = catalog[moduleKey]?.metrics || {}
    const allKeys = Object.keys(rawMetrics)
    if (!allKeys.length) return

    const resolvedFilters = filtersOverride !== undefined ? filtersOverride : activeFilters
    const resolvedFilterData = filterDataOverride !== undefined ? filterDataOverride : filterData

    setLoading(true)
    setResults(prev => {
      const next = { ...prev }
      allKeys.forEach(k => { next[k] = { ...rawMetrics[k], key: k, _loading: true } })
      return next
    })

    const effectiveFilters = buildEffectiveFilters(resolvedFilters, resolvedFilterData)
    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller
    try {
      await runMetricsStream(customerId, allKeys, effectiveFilters, (metric) => {
        if (controller.signal.aborted) return
        setResults(prev => ({ ...prev, [metric.key]: metric }))
      }, controller.signal)
      if (!controller.signal.aborted) setLastRun(new Date())
    } catch (err) {
      if (err.name === 'AbortError') return  // cancelled — ignore
      console.warn('Stream failed, falling back to batch:', err)
      try {
        const data = await runMetrics(customerId, allKeys, effectiveFilters)
        setResults(prev => {
          const next = { ...prev }
          data.forEach(m => { next[m.key] = m })
          return next
        })
        setLastRun(new Date())
      } catch (err2) {
        console.error('Metrics run failed:', err2)
      }
    } finally {
      setLoading(false)
    }
  }, [catalog, customerId])

  const filtersInitRef = useRef(false)
  const prevFiltersRef = useRef(null)
  const abortRef = useRef(null)

  // Reset all run-state when customer changes so refs from the previous
  // customer don't cause stale filter comparisons or spurious runModule calls.
  useEffect(() => {
    filtersInitRef.current = false
    prevFiltersRef.current = null
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null }
    setResults({})
    setLastRun(null)
  }, [customerId])
  useEffect(() => {
    if (!catalog || !activeModule) return
    filtersInitRef.current = true
    prevFiltersRef.current = activeFilters
    runModule(activeModule, activeFilters, filterData)
  }, [catalog, activeModule]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!catalog || !activeModule) return
    if (!filtersInitRef.current) return
    const prev = JSON.stringify(prevFiltersRef.current)
    const curr = JSON.stringify(activeFilters)
    if (prev === curr) return
    prevFiltersRef.current = activeFilters
    const t = setTimeout(() => runModule(activeModule, activeFilters, filterData), 400)
    return () => clearTimeout(t)
  }, [activeFilters]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Rail is positionally aware of the panel that actually scrolls ──
  // The page scrolls inside an inner panel (portal: .cportal-content, consultant:
  // .cpg-content), not the window, so the old `max-height: 100vh - 32px` made the
  // rail taller than the visible area: cut off at the bottom, and pushed up under
  // the tab bar once the cards ran out. Size it to the visible part of the panel
  // (and of the layout, so it never overruns the end of the cards) on every
  // scroll and resize.
  const rootRef = useRef(null)
  const [selecting, setSelecting] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState(() => new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const railRef = useRef(null)
  const scrollerRef = useRef(null)
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    let scroller = root.parentElement
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement
    scrollerRef.current = scroller
    const target = scroller || window
    let frame = 0
    const fit = () => {
      frame = 0
      const rail = railRef.current
      const layout = root.querySelector('.wt-layout')
      if (!rail || !layout) return
      const sr = scroller ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight }
      const padTop = scroller ? parseFloat(getComputedStyle(scroller).paddingTop) || 0 : 0
      const stuckTop = sr.top + padTop + (parseFloat(getComputedStyle(rail).top) || 0)
      // Where the rail's top really is -- but never above where it sticks. Near the
      // end of the cards the layout pushes a too-tall rail up; measuring from the
      // stuck line shrinks it back to fit instead of chasing it upward.
      const railTop = Math.max(rail.getBoundingClientRect().top, stuckTop)
      const lay = layout.getBoundingClientRect()
      const avail = Math.min(sr.bottom, lay.bottom) - railTop - 16
      rail.style.maxHeight = `${Math.max(160, Math.floor(avail))}px`
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(fit) }
    fit()
    target.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    // Anything above the rail that changes height (banners, the filter strip) moves it.
    if (ro) { ro.observe(root); if (scroller) { ro.observe(scroller); if (scroller.firstElementChild) ro.observe(scroller.firstElementChild) } }
    return () => {
      if (frame) cancelAnimationFrame(frame)
      target.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      if (ro) ro.disconnect()
    }
  }, [catalog])

  // Keep the selected category visible in the rail.
  useEffect(() => {
    // Scroll the rail only -- scrollIntoView would also scroll the cards panel.
    const rail = railRef.current
    const el = rail?.querySelector('.wt-rail-item.active')
    if (!el) return
    const r = rail.getBoundingClientRect(), e = el.getBoundingClientRect()
    if (e.top < r.top) rail.scrollTop -= (r.top - e.top) + 8
    else if (e.bottom > r.bottom) rail.scrollTop += (e.bottom - r.bottom) + 8
  }, [activeModule])

  if (!catalog && catalogError) {
    return <LoadError what="the metric catalog" error={catalogError} onRetry={() => setCatalogTry(n => n + 1)} />
  }
  if (!catalog) {
    return (
      <div className="wt-loading-full">
        <div className="wt-spinner" />
        <span>Loading metric catalog…</span>
      </div>
    )
  }

  // Present-domain gate: show only modules the customer has data for (register tab
  // always kept; before present_domains resolves, show all rather than flash empty).
  const modules = Object.entries(catalog).filter(
    ([, mod]) => mod?._synthetic || !presentDomains || presentDomains.includes(mod?.data_source || 'hcm')
  )

  // For the active module, get all metrics then apply curation
  const rawModuleMetrics = activeModule ? Object.entries(catalog[activeModule]?.metrics || {}) : []
  const currentModuleMetrics = applyModuleCuration(activeModule, rawModuleMetrics)

  const populatedCount = currentModuleMetrics.filter(([k]) => {
    const r = results[k]
    return r && r.value !== null && r.value !== undefined && !r.error && !r._loading
  }).length

  // For module tabs: show count of visible metrics in that module
  const visibleInModule = (moduleKey) => {
    const keys = Object.keys(catalog[moduleKey]?.metrics || {})
    if (!hasCuration) return keys.length
    return keys.filter(k => !curationMap[k] || curationMap[k].visible !== false).length
  }

  const curatedCount = hasCuration ? curation.entries.filter(e => e.visible !== false).length : null
  const totalCatalogCount = modules.reduce((a, [, m]) => a + Object.keys(m.metrics || {}).length, 0)

  const moduleCount = (key) => {
    const modPop = Object.keys(catalog[key]?.metrics || {}).filter(k => { const r = results[k]; return r && r.value !== null && !r.error && !r._loading }).length
    return modPop || visibleInModule(key)
  }

  // Category rail: modules grouped by their catalog `group`, groups in first-seen catalog order.
  const railGroups = []
  modules.forEach(([key, mod]) => {
    const name = mod.group || 'Other'
    let g = railGroups.find(x => x.name === name)
    if (!g) { g = { name, items: [] }; railGroups.push(g) }
    g.items.push([key, mod])
  })
  const railQ = railQuery.trim().toLowerCase()
  const visibleRailGroups = railGroups
    .map(g => ({ ...g, items: g.items.filter(([key, mod]) => !railQ || (mod.label || key).toLowerCase().includes(railQ)) }))
    .filter(g => g.items.length)

  // ── Multi-card download (cardExport.downloadCards) ──
  const toggleCardSelect = (k) => setSelectedKeys(prev => {
    const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next
  })
  const stopSelecting = () => { setSelecting(false); setSelectedKeys(new Set()) }
  const selectAllCards = () => setSelectedKeys(new Set(
    currentModuleMetrics.filter(([k]) => results[k] && results[k].value !== null && results[k].value !== undefined && !results[k].error).map(([k]) => k)))
  const downloadSelected = async () => {
    if (!selectedKeys.size) return
    setBulkBusy(true)
    try {
      const root = rootRef.current
      const items = currentModuleMetrics.filter(([k]) => selectedKeys.has(k)).map(([k, def]) => ({
        metric: { ...def, ...(results[k] || {}), key: k },
        el: root?.querySelector(`.wt-metric-card[data-metric-key="${k}"]`) || null,
        category: activeMod?.label || '',
      }))
      await downloadCards({ customerId, customerName: coName, items, filters: effectiveFilters })
      stopSelecting()
    } catch (err) {
      console.error('Download failed:', err)
    } finally { setBulkBusy(false) }
  }

  const selectModule = (key) => {
    stopSelecting()
    setActiveModule(key)
    if (onModuleChange) onModuleChange(key)
    // A new category starts at its first card, not wherever the last one was scrolled.
    const scroller = scrollerRef.current
    const layout = rootRef.current?.querySelector('.wt-layout')
    if (scroller && layout) {
      const offset = layout.getBoundingClientRect().top - scroller.getBoundingClientRect().top
      if (offset < 0) scroller.scrollTop += offset
    }
  }
  const toggleGroup = (name) => setCollapsedGroups(prev => {
    const next = new Set(prev)
    next.has(name) ? next.delete(name) : next.add(name)
    return next
  })
  const activeMod = activeModule ? catalog[activeModule] : null

  return (
    <div className="wt-root" ref={rootRef}>
      <div className="wt-layout">

      {/* Category Rail */}
      <nav className="wt-rail" ref={railRef} aria-label="Metric categories">
        <input
          className="wt-rail-search"
          type="search"
          placeholder="Find a category"
          aria-label="Find a category"
          value={railQuery}
          onChange={e => setRailQuery(e.target.value)}
        />
        {visibleRailGroups.map(g => {
          const isClosed = collapsedGroups.has(g.name) && !railQ
          return (
            <div key={g.name} className={`wt-rail-group ${isClosed ? 'closed' : ''}`}>
              <button type="button" className="wt-rail-group-h" aria-expanded={!isClosed} onClick={() => toggleGroup(g.name)}>
                <span className="wt-rail-chev">▾</span>
                {g.name}
              </button>
              {!isClosed && g.items.map(([key, mod]) => (
                <button
                  key={key}
                  type="button"
                  className={`wt-rail-item ${activeModule === key ? 'active' : ''}`}
                  aria-current={activeModule === key ? 'page' : undefined}
                  onClick={() => selectModule(key)}
                >
                  <span className="wt-rail-label">{mod.label}</span>
                  {!mod._synthetic && <span className="wt-mod-count">{moduleCount(key)}</span>}
                </button>
              ))}
            </div>
          )
        })}
        {!visibleRailGroups.length && (
          <div className="wt-rail-empty">No category matches “{railQuery}”.</div>
        )}
      </nav>

      <div className="wt-main">

      {/* Narrow screens: the rail collapses into a dropdown */}
      <select
        className="wt-rail-picker"
        aria-label="Metric category"
        value={activeModule || ''}
        onChange={e => selectModule(e.target.value)}
      >
        {railGroups.map(g => (
          <optgroup key={g.name} label={g.name}>
            {g.items.map(([key, mod]) => (
              <option key={key} value={key}>{mod._synthetic ? mod.label : `${mod.label} (${moduleCount(key)})`}</option>
            ))}
          </optgroup>
        ))}
      </select>

      {/* Category header */}
      <div className="wt-module-head">
        <div className="wt-module-title">
          {activeMod?.group && <div className="wt-module-group">{activeMod.group}</div>}
          <h2>{activeMod?.label}</h2>
        </div>
        <div style={{ flex: 1 }} />

        {/* Curation button — consultant only */}
        {consultantMode && (
          <button
            className={`wt-curate-btn ${hasCuration ? 'wt-curate-btn--active' : ''}`}
            onClick={() => setShowCuration(true)}
            title="Customize which metrics show in the portal"
          >
            ✦ Curate
            {hasCuration && (
              <span className="wt-curate-count">{curatedCount}/{totalCatalogCount}</span>
            )}
          </button>
        )}

        {activeModule !== 'register' && (selecting ? (
          <>
            <button className="wt-run-all-btn" onClick={selectAllCards}>Select all</button>
            <button className="wt-run-all-btn wt-desc-toggle--on" onClick={downloadSelected} disabled={!selectedKeys.size || bulkBusy}>
              {bulkBusy ? <><span className="wt-btn-spinner" /> Preparing…</> : `Download ${selectedKeys.size || ''} (.zip)`}
            </button>
            <button className="wt-run-all-btn" onClick={stopSelecting}>Cancel</button>
          </>
        ) : (
          <button className="wt-run-all-btn" onClick={() => setSelecting(true)} title="Pick several cards and download them together">Select cards</button>
        ))}

        <button
          className={`wt-run-all-btn wt-desc-toggle ${showDescriptions ? 'wt-desc-toggle--on' : ''}`}
          onClick={toggleDescriptions}
          aria-pressed={showDescriptions}
          title={showDescriptions ? 'Hide metric descriptions' : 'Show metric descriptions'}
        >
          Descriptions {showDescriptions ? 'on' : 'off'}
        </button>

        {activeModule !== 'register' && (
          <button
            className="wt-run-all-btn"
            onClick={() => activeModule && runModule(activeModule, activeFilters, filterData)}
            disabled={loading}
          >
            {loading ? <><span className="wt-btn-spinner" /> Running…</> : '↻ Refresh'}
          </button>
        )}
        {activeModule !== 'register' && lastRun && (
          <span className="wt-last-run">
            {populatedCount}/{currentModuleMetrics.length} · {lastRun.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {/* Curation notice — shown when portal view has active curation */}
      {!consultantMode && hasCuration && (
        <div className="wt-curation-notice">
          Showing {curatedCount} of {totalCatalogCount} metrics · configured by your Sempirical Account Representative
        </div>
      )}

      {/* Register tab — full-width, bypasses metric grid */}
      {activeModule === 'register' && (
        <RegisterTab customerId={customerId} onOpenEmployee={onOpenEmployee} />
      )}

      {/* Grid — all other modules */}
      {activeModule !== 'register' && (
      <div className="wt-scroll-area">
        <div className="wt-metrics-grid">
          {currentModuleMetrics.map(([key, def]) => {
            const result = results[key]
            const merged = result ? { ...def, ...result, key, module_key: activeModule } : { ...def, key, module_key: activeModule, value: null }
            return (
              <MetricCard
                key={key}
                metric={merged}
                moduleKey={activeModule}
                customerId={customerId}
                filters={effectiveFilters}
                onDrill={setDrillTarget}
                onEdit={setEditTarget}
                showDescriptions={showDescriptions}
                selecting={selecting}
                selected={selectedKeys.has(key)}
                onToggleSelect={toggleCardSelect}
              />
            )
          })}
        </div>
      </div>
      )}

      </div>
      </div>

      {drillTarget && <DrillModal customerId={customerId} companyName={coName} metric={drillTarget} month={drillTarget._month || null} filters={effectiveFilters} onClose={() => setDrillTarget(null)} onOpenEmployee={onOpenEmployee} />}
      {editTarget && <EditPanel
              metric={editTarget}
              customerId={customerId}
              companyName={coName}
              onClose={() => setEditTarget(null)}
              onSaved={(key, overrides) => {
                setResults(prev => ({
                  ...prev,
                  [key]: { ...(prev[key] || {}), ...overrides }
                }))
              }}
            />}
      {showCuration && (
        <CurationPanel
          catalog={catalog}
          curation={curation}
          customerId={customerId}
          onSave={(entries) => setCuration({ entries })}
          onClose={() => setShowCuration(false)}
        />
      )}
    </div>
  )
}





